import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  merchantSalesAgentCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type { MerchantSalesAgentDto } from "@/features/merchant/api/dto";
import type { SalesAgentStatus } from "@/features/shared/api/enums";

/**
 * Force le statut d'un agent de vente
 * (`POST /MerchantSalesAgents/{id}/status?status=...`).
 */
export const useSetSalesAgentStatus = (merchantId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "sales-agents", "set-status"],
    mutationFn: ({
      salesAgentId,
      status,
    }: {
      salesAgentId: string;
      status: SalesAgentStatus;
    }) =>
      mutateData({
        url: `${merchantEndpoints.merchantSalesAgents.status(salesAgentId)}?status=${status}`,
        method: "POST",
      }) as Promise<MerchantSalesAgentDto>,
    onSuccess: (_data, { salesAgentId }) => {
      return invalidateAfterMutation(
        queryClient,
        merchantSalesAgentCacheKeys(merchantId, salesAgentId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSetSalesAgentStatus failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
